import { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CallbackBand, Footer, HOME_ROUTES, SiteNav } from '../components/Layout.jsx';
import { ArrowRight, BriefcaseIcon, HomeIcon, MapPin, Search } from '../components/icons.jsx';
import { HOME_CONTENT, SEARCH_PLACEHOLDER } from '../lib/homeContent.js';

/**
 * The site's front door.
 *
 * Home and business are one page with two states rather than two pages: the
 * Figma gives them an identical structure, and the hero toggle is meant to
 * flip between them in place. Each still has its own URL so the business side
 * can be linked to directly — `/for-business` rather than `/business`, because
 * `business` is a live journey slug and `/:slug` already routes to the
 * journey.
 */
export default function HomePage({ brand = 'home' }) {
  const content = HOME_CONTENT[brand] ?? HOME_CONTENT.home;
  const [postcode, setPostcode] = useState('');
  const navigate = useNavigate();

  const handleSearch = (event) => {
    event.preventDefault();
    // An empty box still moves the customer forward — the journey's own first
    // step asks for the postcode and validates it properly. Bouncing them here
    // with an error would be a dead end on the page whose only job is to start
    // the journey.
    navigate(`/${content.journeySlug}`, {
      state: { postcode: postcode.trim() || undefined },
    });
  };

  return (
    <div className="page home" data-brand={brand}>
      <div className="home-hero">
        <div className="home-hero__swish" aria-hidden="true" />
        <img className="home-hero__image" src={content.hero.image} alt="" aria-hidden="true" />

        <div className="home-hero__inner">
          <SiteNav brand={brand} active={brand} />

          <div className="home-hero__content">
            <div className="segmented" role="group" aria-label="Choose what you need broadband for">
              <Link
                className="segmented__option"
                to={HOME_ROUTES.home}
                data-active={brand === 'home' ? 'true' : undefined}
                aria-current={brand === 'home' ? 'page' : undefined}
              >
                <HomeIcon size={18} />
                <span>For Home</span>
              </Link>
              <Link
                className="segmented__option"
                to={HOME_ROUTES.business}
                data-active={brand === 'business' ? 'true' : undefined}
                aria-current={brand === 'business' ? 'page' : undefined}
              >
                <BriefcaseIcon size={18} />
                <span>For Business</span>
              </Link>
            </div>

            <h1 className="home-hero__title">
              {content.hero.highlight
                ? content.hero.title
                    .split(content.hero.highlight)
                    .map((part, i, all) => (
                      // Index keys are safe here: the list is derived from a
                      // constant string and never reorders.
                      <Fragment key={i}>
                        {part}
                        {i < all.length - 1 && (
                          <span className="home-hero__highlight">{content.hero.highlight}</span>
                        )}
                      </Fragment>
                    ))
                : content.hero.title}
            </h1>

            <p className="home-hero__subtitle">{content.hero.subtitle}</p>

            <form className="home-search" onSubmit={handleSearch}>
              <span className="home-search__pin" aria-hidden="true">
                <MapPin size={24} />
              </span>
              <label className="visually-hidden" htmlFor="home-postcode">
                Your postcode
              </label>
              <input
                id="home-postcode"
                className="home-search__input"
                name="postcode"
                placeholder={SEARCH_PLACEHOLDER}
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                autoComplete="postal-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                maxLength={8}
                enterKeyHint="search"
              />
              <button className="home-search__button" type="submit">
                <Search size={18} />
                <span>Search</span>
              </button>
            </form>
          </div>
        </div>
      </div>

      <CallbackBand brand={brand} />

      <section className="benefits">
        <div className="section-header">
          <span className="eyebrow">{content.benefits.eyebrow}</span>
          <h2 className="section-header__title">{content.benefits.title}</h2>
          <p className="section-header__subtitle">{content.benefits.subtitle}</p>
        </div>

        <ul className="benefits__grid">
          {content.benefits.cards.map(({ icon: Icon, title, body }) => (
            <li className="benefit" key={title}>
              <span className="benefit__icon" aria-hidden="true">
                <Icon size={28} />
              </span>
              <h3 className="benefit__title">{title}</h3>
              <p className="benefit__body">{body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="bento">
        <div className="bento__swish" aria-hidden="true" />
        <div className="section-header section-header--on-dark">
          <span className="eyebrow eyebrow--on-dark">{content.bento.eyebrow}</span>
          <h2 className="section-header__title">{content.bento.title}</h2>
          <p className="section-header__subtitle">{content.bento.subtitle}</p>
        </div>

        <ul className="bento__grid">
          {content.bento.tiles.map((tile) => (
            <li className="bento__tile" data-wide={tile.wide ? 'true' : undefined} key={tile.title}>
              <img src={tile.image} alt="" aria-hidden="true" loading="lazy" />
              <div className="bento__caption">
                <h3>{tile.title}</h3>
                <p>{tile.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="cta-strip">
        <div className="cta-strip__inner">
          <h2 className="cta-strip__headline">{content.cta}</h2>
          <Link className="cta-strip__button" to={`/${content.journeySlug}`}>
            <span>Start Now</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
